'use client';

import { useDragControls } from 'motion/react';
import { PointerEvent, RefObject, createContext, useCallback } from 'react';

export interface MovableContextValue {
  dragControls: ReturnType<typeof useDragControls>;
}

export const MovableContext = createContext<MovableContextValue | null>(null);

export const initial = { opacity: 0 };
export const animate = { opacity: 1, transition: { ease: 'easeOut' as const, duration: 0.2 } };
export const exit = { opacity: 0, transition: { ease: 'easeIn' as const, duration: 0.15 } };

export const loadDomMax = () => import('motion/react').then(({ domMax }) => domMax);

export const constraintsStyle = {
  pos: 'fixed',
  inset: 0,
  zIndex: 'modal',
  pointerEvents: 'none',
} as const;

export const headerDragStyle = {
  cursor: 'grab',
  userSelect: 'none',
  _active: { cursor: 'grabbing' },
} as const;

export function useDragHandlePointerDown(dragControls: ReturnType<typeof useDragControls> | undefined) {
  return useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, input, textarea, select, a, [role="tab"], [role="slider"], [data-no-drag]')) return;
      dragControls?.start(e);
    },
    [dragControls],
  );
}

export function getMovableDialogMotionProps(
  dragControls: ReturnType<typeof useDragControls>,
  constraintsRef: RefObject<HTMLDivElement | null>,
) {
  return {
    role: 'dialog' as const,
    'aria-modal': 'false' as const,
    drag: true,
    dragControls,
    dragListener: false,
    dragConstraints: constraintsRef,
    dragMomentum: false,
    dragElastic: 0.05,
    initial: { ...initial, scale: 0.96 },
    animate: { ...animate, scale: 1 },
    exit: { ...exit, scale: 0.96 },
    style: { willChange: 'transform' as const },
    transformTemplate: (_: unknown, generated: string) => `translate(-50%, -50%) ${generated}`,
  };
}
